/* eslint-disable no-magic-numbers */
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  Injector,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatSelectChange } from '@angular/material/select';
import { Store } from '@ngxs/store';
import { first, Subscription, take, tap } from 'rxjs';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { MainTableFilterIdentification } from 'src/app/core/models/main-table-filter-identification.model';
import { ApplyPaging } from 'src/app/core/state/main-table-filter.actions';
import { MainTableFilterState } from 'src/app/core/state/main-table-filter.state';
import {
  defaultInitialPageSize,
  defaultPageSizes,
} from '../../../core/table-config/table-paging-defaults';
import { IPageModel } from '../../models/page.model';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
} from '../../models/main-table-filter.model';

const KEEP_PAGE_FOR_MODULES = [
  MainTableFilterModulesEnum.DueListWarnings,
  MainTableFilterModulesEnum.DueListMyWarnings,
  MainTableFilterModulesEnum.DueListCompetences,
];

@Component({
  selector: 'app-main-table-paginator',
  templateUrl: './main-table-paginator.component.html',
  styleUrls: ['./main-table-paginator.component.scss'],
  standalone: false,
})
export class MainTablePaginatorComponent implements OnChanges, OnInit, OnDestroy {
  private readonly injector = inject(Injector);

  @Input() totalPages: any;
  @Input() additional: any;
  @Input() showPageSizeSelector = true;
  @Input() initialPageSize = defaultInitialPageSize;
  @Input() moduleName: MainTableFilterModulesEnum;
  @Input() skipInitialLoad = false;
  @Input() initialFilters: MainTableFilterSearchByModel[] = [];

  pageSizeOptions = defaultPageSizes;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  currentPageDetails: IPageModel;

  private modulePageSizeSub?: Subscription;
  private pagingDetailsSub?: Subscription;
  private filterIdentification: MainTableFilterIdentification;

  constructor(private store: Store) {}

  ngOnChanges(_changes: SimpleChanges): void {
    if (
      _changes.moduleName?.currentValue !== _changes.moduleName?.previousValue &&
      !this.skipInitialLoad &&
      !_changes.moduleName?.firstChange
    ) {
      this.listenToPagingDetailsChanges();
      this.subscribeModulePageSize();
    }
    if (
      this.filterIdentification &&
      _changes.additional &&
      !_changes.additional.firstChange &&
      _changes.additional.currentValue !== _changes.additional.previousValue
    ) {
      this.filterIdentification = new MainTableFilterIdentification(
        this.moduleName,
        this.additional
      );
      if (this.shouldSyncAdditionalWithoutDispatch()) {
        this.syncPagingDetailsFromStoreWithoutDispatch();
      } else {
        this.listenToPagingDetailsChanges();
      }
    }
    if (this.currentPageDetails) {
      this.checkIfPreviousAndNextPageExist();
      this.checkAmountOfTotalPages();
    }
  }

  ngOnInit(): void {
    this.filterIdentification = new MainTableFilterIdentification(this.moduleName, this.additional);
    if (!this.skipInitialLoad) {
      this.listenToPagingDetailsChanges();
    }
    this.subscribeModulePageSize();
  }

  ngOnDestroy(): void {
    this.modulePageSizeSub?.unsubscribe();
    this.pagingDetailsSub?.unsubscribe();
  }

  previousPage(): void {
    this.currentPageDetails.pageIndex--;
    this.checkIfPreviousAndNextPageExist();
    this.store.dispatch(
      new ApplyPaging(
        this.moduleName,
        this.currentPageDetails.pageIndex,
        this.currentPageDetails.pageSize,
        this.additional
      )
    );
  }

  nextPage(): void {
    this.currentPageDetails.pageIndex++;
    this.checkIfPreviousAndNextPageExist();
    this.store.dispatch(
      new ApplyPaging(
        this.moduleName,
        this.currentPageDetails.pageIndex,
        this.currentPageDetails.pageSize,
        this.additional
      )
    );
  }

  pageSizeChangeEvent(event: MatSelectChange): void {
    this.pageSizeChange(event.value);
  }

  private pageSizeChange(pageSize: number): void {
    this.currentPageDetails.pageSize = pageSize;
    this.currentPageDetails.pageIndex = 0;
    this.checkIfPreviousAndNextPageExist();
    this.checkAmountOfTotalPages();
    this.store.dispatch(
      new ApplyPaging(
        this.moduleName,
        this.currentPageDetails.pageIndex,
        this.currentPageDetails.pageSize,
        this.additional
      )
    );
  }

  private subscribeModulePageSize(): void {
    this.modulePageSizeSub?.unsubscribe();
    const pageSizeSig = this.store.selectSignal(
      MainTableFilterState.getModulePageSize(this.moduleName)
    );
    this.modulePageSizeSub = toObservable(pageSizeSig, { injector: this.injector })
      .pipe(
        tap((pageSize) => {
          if (pageSize && this.currentPageDetails) {
            this.currentPageDetails.pageSize = pageSize;
          }
        })
      )
      .subscribe();
  }

  private shouldSyncAdditionalWithoutDispatch(): boolean {
    if (!this.moduleName) {
      return false;
    }
    const filterIdentification = new MainTableFilterIdentification(
      this.moduleName,
      this.additional
    );
    const pagingDetails = this.store.selectSnapshot(
      MainTableFilterState.getPagingDetails(filterIdentification)
    );
    const currentFilter = this.store.selectSnapshot(MainTableFilterState.getCurrentFilterDetails);
    return !!pagingDetails && pagingDetails === currentFilter;
  }

  private syncPagingDetailsFromStoreWithoutDispatch(): void {
    if (!this.moduleName) {
      return;
    }
    const filterIdentification = new MainTableFilterIdentification(
      this.moduleName,
      this.additional
    );
    const pagingDetails = this.store.selectSnapshot(
      MainTableFilterState.getPagingDetails(filterIdentification)
    );
    if (!pagingDetails) {
      return;
    }
    const pageSize =
      this.store.selectSnapshot(MainTableFilterState.getModulePageSize(this.moduleName)) ??
      pagingDetails.pageSize ??
      this.initialPageSize;
    this.currentPageDetails = new IPageModel(pagingDetails.pageIndex, pageSize);
    this.checkIfPreviousAndNextPageExist();
    this.checkAmountOfTotalPages();
  }

  private listenToPagingDetailsChanges(): void {
    let lastPagingDetails: MainTableFilterModel | undefined;
    this.filterIdentification = new MainTableFilterIdentification(this.moduleName, this.additional);
    this.pagingDetailsSub?.unsubscribe();
    const pagingSig = this.store.selectSignal(
      MainTableFilterState.getPagingDetails(this.filterIdentification)
    );
    this.pagingDetailsSub = toObservable(pagingSig, { injector: this.injector })
      .pipe(
        tap((pagingDetails: MainTableFilterModel | undefined) => {
          lastPagingDetails = pagingDetails;
          // sending the initial request with the default page index and page size for the module
          this.currentPageDetails = new IPageModel(
            this.store.selectSnapshot(MainTableFilterState.getCurrentPageIndex),
            this.store.selectSnapshot(MainTableFilterState.getModulePageSize(this.moduleName)) ||
              pagingDetails?.pageSize ||
              this.initialPageSize
          );
          this.checkIfPreviousAndNextPageExist();
          this.checkAmountOfTotalPages();
        }),
        first(),
        tap(() => {
          let currentPageIndex = this.store.selectSnapshot(
            MainTableFilterState.getCurrentPageIndex
          );
          if (KEEP_PAGE_FOR_MODULES.includes(this.moduleName)) {
            currentPageIndex = lastPagingDetails?.pageIndex ?? currentPageIndex;
          }

          // Pass initial filters to ApplyPaging to avoid double request
          this.store.dispatch(
            new ApplyPaging(
              this.moduleName,
              currentPageIndex,
              this.currentPageDetails.pageSize,
              this.additional,
              this.initialFilters && this.initialFilters.length > 0
                ? this.initialFilters
                : undefined
            )
          );
        }),
        take(1)
      )
      .subscribe();
  }

  private checkIfPreviousAndNextPageExist(): void {
    this.previousPageExists();
    this.nextPageExists();
  }

  private checkAmountOfTotalPages(): void {
    if (
      (this.totalPages === 1 && this.currentPageDetails.pageIndex !== 0) ||
      (this.totalPages > 0 && this.currentPageDetails.pageIndex + 1 > this.totalPages)
    ) {
      this.currentPageDetails.pageIndex = 0;
      this.checkIfPreviousAndNextPageExist();
      // send out a request with updated pageIndex value
      this.store.dispatch(
        new ApplyPaging(
          this.moduleName,
          this.currentPageDetails.pageIndex,
          this.currentPageDetails.pageSize,
          this.additional
        )
      );
    } else {
      this.currentPageDetails.pageIndex = this.store.selectSnapshot(
        MainTableFilterState.getCurrentPageIndex
      );
    }
  }

  private previousPageExists(): void {
    this.hasPreviousPage = this.currentPageDetails.pageIndex > 0;
  }

  private nextPageExists(): void {
    this.hasNextPage = this.totalPages - 1 > this.currentPageDetails.pageIndex;
  }
}
